import {
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  checkForAnyErrors, getDefaultErrorSchema, hasError
} from '../helpers/errorHandlingUtil';
import { getCalculatorLocalStorage, saveStorageObject } from '../helpers/localStorageUtil';
import { MIN_AREA_NUMBER } from '../constants';
import { getCoverageAndQuantity, getDefaultArea } from '../helpers/helpers';

const getCleanValue = (value) => {
  if (value) {
    return value === '.' ? '0' : Number(value).toString();
  }
  return '';
};

const cleanNumberValues = ({ areas, calculatorType }) => {
  const cleanedAreas = areas.map((area) => {
    return {
      ...area,
      length: getCleanValue(area.length),
      width: getCleanValue(area.width),
      depth: calculatorType === 'Volume' ? getCleanValue(area.depth) : '',
      squareFootage: {
        value: getCleanValue(area.squareFootage.value),
        changed: area.squareFootage.changed
      }
    };
  });
  return cleanedAreas;
};

export const useCalculator = ({
  allowAdditionalCoverage, calculatorType, info, lineItemName, unitsPerCase
}) => {
  const { segment } = useContext(ExperienceContext);
  const [calculate, setCalculate] = useState({
    calcByArea: false,
    calcByLengthWidth: true,
    // TODO: remove this default when pipcalculator addAdditionalCoverage values are populated by IDM
    addAdditionalCoverage: segment !== 'flooring',
    isClicked: false,
    quantity: 0,
    coverage: 0,
    step: 1,
    areas: [
      getDefaultArea({ index: 0, calculatorType, lineItemName })
    ],
    areaErrors: [
      getDefaultErrorSchema()
    ],
    isFromLocalStorage: false,
    changesAwaitingLocalStorage: false
  });

  const { storageKey, storageObj } = getCalculatorLocalStorage({
    classInfo: info
  });

  /*  This is required due to refs persisting when component unmounts.
  Which is required for saving to localStorage when unmounting */
  const calculateRef = useRef(calculate);
  const storageKeyRef = useRef(storageKey);

  const saveToLocalStorageFunc = () => {
    const { changesAwaitingLocalStorage } = calculateRef.current;

    if (changesAwaitingLocalStorage) {
      // If calcByArea is false, then we need to re-calculate squareFootage
      if (!calculateRef.current.calcByArea) {
        calculateRef.current = {
          ...calculateRef.current,
          areas: calculateRef.current.areas.map((area) => {
            const { length, squareFootage, width } = area;
            return {
              ...area,
              squareFootage: {
                ...squareFootage,
                value: (length && width) ? (length * width).toString() : squareFootage.value
              }
            };
          })
        };
      }
      const { areas, calcByLengthWidth, calcByArea } = calculateRef.current;

      const cleanedAreas = cleanNumberValues({ areas, calculatorType });
      const strictErrors = checkForAnyErrors({
        calculatorType, calcByLengthWidth, calcByArea, areas: cleanedAreas
      });
      if (!hasError(strictErrors, calcByArea) && storageKeyRef.current) {
        const cleanedCalculate = {
          ...calculateRef.current,
          areas: cleanedAreas
        };
        saveStorageObject(storageKeyRef.current, cleanedCalculate);
        setCalculate({
          ...cleanedCalculate,
          changesAwaitingLocalStorage: false
        });
      }
    }
  };

  useEffect(() => {
    if (storageObj) {
      setCalculate({
        ...calculate,
        ...storageObj,
        areaErrors: new Array(storageObj.areas.length).fill('').map(() => getDefaultErrorSchema()),
        isFromLocalStorage: true
      });
    }
  }, [storageObj === null]);

  useEffect(() => {
    calculateRef.current = calculate;
  }, [calculate]);

  useEffect(() => {
    storageKeyRef.current = storageKey;
  }, [storageKey]);

  useEffect(() => {
    window.addEventListener('beforeunload', saveToLocalStorageFunc);
    return () => {
      saveToLocalStorageFunc();
      window.removeEventListener('beforeunload', saveToLocalStorageFunc);
    };
  }, []);

  const addElement = () => {
    const {
      areas,
      areaErrors
    } = calculate;
    const newAreas = JSON.parse(JSON.stringify(areas));
    newAreas.push(getDefaultArea({ index: areas.length, calculatorType, lineItemName }));
    const newAreaErrors = JSON.parse(JSON.stringify(areaErrors));
    newAreaErrors.push(getDefaultErrorSchema());
    setCalculate({
      ...calculate,
      areas: newAreas,
      areaErrors: newAreaErrors,
      step: areas.length
    });
    // Scroll to latest area
    setTimeout(() => {
      const sectionDivs = document.querySelectorAll('#calc-input-sections > div');
      const lastSectionOffsetTop = sectionDivs[sectionDivs.length - 1].offsetTop;
      const sections = document.getElementById('calc-input-sections');
      if (typeof sections.scroll === 'function') { // tests break, so added this check as a temp fix at the least
        sections.scroll({
          top: lastSectionOffsetTop,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const calculateTotal = ({ isClick, onCalculate }) => {
    const {
      addAdditionalCoverage,
      calcByArea,
      calcByLengthWidth,
      areas
    } = calculate;

    const cleanedAreas = cleanNumberValues({ areas, calculatorType });
    const strictErrors = checkForAnyErrors({
      calculatorType, calcByLengthWidth, calcByArea, areas: cleanedAreas
    });
    const isError = hasError(strictErrors, calcByArea);
    // If there's an error and it's a soft calculate (user didn't click), then we shouldn't set state
    if (isError && !isClick) {
      return;
    }
    const sizeValue = +unitsPerCase;
    let { coverage, quantity } = calculate;

    if (!isError) {
      ({ coverage, quantity } = getCoverageAndQuantity({
        addAdditionalCoverage,
        allowAdditionalCoverage,
        areas: cleanedAreas,
        calcByArea,
        calcByLengthWidth,
        calculatorType,
        sizeValue
      }));
    }
    const ret = {
      ...calculate,
      quantity,
      coverage,
      areaErrors: strictErrors,
      isClicked: !isError && isClick,
      areas: !isError ? cleanedAreas : areas
    };
    if (onCalculate) {
      onCalculate({
        addAdditionalCoverage: ret.addAdditionalCoverage,
        quantity: ret.quantity,
        coverage: ret.coverage,
        error: isError
      });
    }
    setCalculate(ret);
  };

  const removeInputElement = (index) => {
    const { areas } = calculate;
    if (areas.length > MIN_AREA_NUMBER) {
      setCalculate((prevState) => {
        const {
          areas: prevAreas,
          areaErrors: prevAreaErrors
        } = prevState;
        const areaName = prevAreas[index].name;
        const areaHasDupeNameError = prevAreaErrors[index].name;
        prevAreas.splice(index, 1);
        prevAreaErrors.splice(index, 1);
        if (areaHasDupeNameError) {
          const dupeAreas = prevAreas.filter((dupeArea) => dupeArea.name === areaName);
          if (dupeAreas.length === 1) { // If there's only one "dupe name" left, set error to false
            prevAreas.forEach((dupeArea, dupeIndex) => {
              if (dupeArea.name === areaName) {
                prevAreaErrors[dupeIndex].name = false;
              }
            });
          }
        }
        const ret = {
          ...calculate,
          step: prevAreas.length,
          changesAwaitingLocalStorage: true,
          areaErrors: prevAreaErrors,
          areas: prevAreas.map((area, areaIndex) => {
            const lineItemNameRegex = new RegExp(`${lineItemName} [0-9]`, 'i');
            const newName = lineItemName && lineItemNameRegex.test(area.name)
              ? `${lineItemName} ${areaIndex + 1}`
              : area.name;
            return {
              ...area,
              key: `${areaIndex}`,
              name: newName
            };
          })
        };
        calculateRef.current = ret;
        saveToLocalStorageFunc();
        return ret;
      });
    }
  };

  return [calculate, {
    addElement, calculateTotal, saveToLocalStorageFunc, removeInputElement, setCalculate
  }];
};
